// ── The document ─────────────────────────────────────────────
/**
 * A page of staff-written content, stored as ProseMirror JSON.
 *
 * The same reasoning as `TermsDoc` — no HTML is stored, served or
 * rendered, so there is nothing to sanitise on a read path and one tree
 * renders on the website, in the phone apps and in the dashboard preview —
 * with a richer vocabulary, because the people writing it are Zuruflex's own
 * staff rather than a partner. A partner's terms keep the narrower
 * `TermsDoc`, so nothing a partner submits can gain an image or a table.
 */
export const CONTENT_NODE_TYPES = [
    'doc',
    'paragraph',
    'heading',
    'bulletList',
    'orderedList',
    'listItem',
    'blockquote',
    'horizontalRule',
    'image',
    'table',
    'tableRow',
    'tableHeader',
    'tableCell',
    'hardBreak',
    'text',
];
export const CONTENT_MARK_TYPES = ['bold', 'italic', 'underline', 'link'];
/** `h1` belongs to the page title, which the page itself renders. */
export const CONTENT_HEADING_LEVELS = [2, 3, 4];
export const EMPTY_CONTENT_DOC = { type: 'doc', content: [] };
// ── The page ─────────────────────────────────────────────────
/**
 * What a page is for. It decides where the public site lists it and who
 * may create it, not how it is edited — every kind shares one editor.
 */
export const CONTENT_KINDS = ['LEGAL', 'HELP', 'PAGE'];
/** The locale every page is written in today. The column exists for later. */
export const CONTENT_DEFAULT_LOCALE = 'en';
/**
 * Slugs a page may not take, because a route of the same name already
 * answers. `/:slug` is the last route on the website, so a collision would
 * not be an error — the page would simply never be reachable.
 */
export const RESERVED_CONTENT_SLUGS = [
    'account',
    'about',
    'api',
    'admin',
    'assets',
    'book',
    'bookings',
    'cars',
    'checkout',
    'find-booking',
    'help',
    'how-it-works',
    'list-your-car',
    'locations',
    'login',
    'long-term',
    'robots.txt',
    'sign-in',
    'sign-up',
    'sitemap.xml',
    'with-driver',
];
export const CONTENT_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
/**
 * Where a page's slug is served from.
 *
 * Partner help sits under its own prefix because the partner phone app
 * deep-links to `/help/partners/<slug>`; everything else is at the root.
 */
export const HELP_PATH_PREFIX = '/help/partners';
export function contentPath(kind, slug) {
    return kind === 'HELP' ? `${HELP_PATH_PREFIX}/${slug}` : `/${slug}`;
}
/** The slug of Zuruflex's agreement with its partners. Its version is what a partner accepts. */
export const PARTNER_TERMS_SLUG = 'partner-terms';
/** Where a page stands, from the three facts the row holds. */
export function statusOf(page) {
    if (page.scheduled && page.hasDraft)
        return 'SCHEDULED';
    if (page.publishedVersion === 0)
        return 'DRAFT_ONLY';
    if (!page.isLive)
        return 'UNPUBLISHED';
    return page.hasDraft ? 'LIVE_WITH_CHANGES' : 'LIVE';
}
/** The soonest and furthest a publish may be scheduled. */
export const SCHEDULE_MIN_LEAD_MINUTES = 2;
export const SCHEDULE_MAX_LEAD_DAYS = 365;
/**
 * Addresses on the site that are not managed pages, so a link to one is not
 * a mistake. The prefixes cover the pages generated from data — a car, a
 * city, an airport — whose full list the dashboard does not hold.
 */
export const SITE_STATIC_PATHS = [
    '/',
    '/about',
    '/account',
    '/bookings',
    '/find-booking',
    '/fleet',
    '/help',
    '/help/partners',
    '/how-it-works',
    '/list-your-car',
    '/locations',
    '/long-term',
    '/saved',
    '/sign-in',
    '/sign-up',
    '/with-driver',
];
export const SITE_DYNAMIC_PREFIXES = ['/fleet/', '/cars/', '/car-rental-', '/airport-car-rental-'];
export const CONTENT_WARNING_CODES = [
    'IMAGE_NO_ALT',
    'PLACEHOLDER',
    'UNKNOWN_LINK',
    'HEADING_SKIP',
    'EMPTY_HEADING',
];
